import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { type ComponentProps, type ReactNode, useState } from "react";
import { DeclineGrantRequestModal } from "../DeclineGrantRequestModal";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useUrlSort } from "../../hooks/useUrlSort";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import { Avatar } from "../Avatar";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
  toggleMultiFilter,
} from "../ListFilter";
import { ListTable } from "../ListTable";
import { SearchField } from "../Settings/shared";
import { SubtleButton } from "../SubtleButton";
import { Text } from "../Text";
import { useToast } from "../Toast";
import { Tooltip } from "../Tooltip";
import { WorkflowBadge } from "../WorkflowBadge";
import { MonitoringLayout } from "./MonitoringLayout";

const HeaderSearch = styled("div")`
  margin-left: auto;
  padding-right: 12px;
  width: 332px;
  max-width: 60%;
  min-width: 0;

  & input[data-search] {
    width: 100%;
    min-width: 0;
  }
`;

type RequestRow = {
  key: string;
  name: (hover: boolean) => ReactNode;
  requester: ReactNode;
  createdAt: ReactNode;
  actions: ReactNode;
  link?: string;
};

type RequestAction = {
  icon: ComponentProps<typeof Icon>["name"];
  label: string;
  onClick: () => void;
};

function requestActions(actions: RequestAction[]): ReactNode {
  const sections = [
    {
      items: actions.map((action) => ({
        type: "button" as const,
        left: <Icon name={action.icon} size={16} />,
        label: action.label,
        onClick: action.onClick,
      })),
    },
  ];
  // stopPropagation so the kebab doesn't trigger the row's open-modal click.
  return (
    <div
      style={{ display: "flex", justifyContent: "flex-end" }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Dropdown
        opener={<SubtleButton icon="more" variant="secondary" />}
        align="bottom"
        minWidth={160}
        sections={sections}
      />
    </div>
  );
}

function cellText(value: string, color: string): ReactNode {
  return value ? (
    <Text ellipsis variant="text-small" color={color}>
      {value}
    </Text>
  ) : null;
}

function requestedWhen(
  createdAt: number,
  formatRelativeTime: (timestamp: number) => string
): ReactNode {
  const relative = formatRelativeTime(createdAt);
  const label = relative.charAt(0).toUpperCase() + relative.slice(1);
  return (
    <Text variant="text-small" color="var(--text-primary)">
      <Tooltip
        content={new Date(createdAt).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      >
        {label}
      </Tooltip>
    </Text>
  );
}

function RequestIcon({
  requester,
  children,
}: {
  requester: ComponentProps<typeof Avatar>["user"] | null | undefined;
  children: ReactNode;
}) {
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      {children}
      {requester ? (
        <div
          style={{
            position: "absolute",
            bottom: -4,
            right: -4,
            borderRadius: "50%",
            boxShadow: "0 0 0 2px var(--cell-bg, var(--surface-primary))",
            lineHeight: 0,
          }}
        >
          <Avatar user={requester} size={16} tooltip={false} />
        </div>
      ) : null}
    </div>
  );
}

export function Requests() {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const { showToast } = useToast();
  const [decliningRequest, setDecliningRequest] = useState<{
    id: string;
    connectorName: string;
  } | null>(null);
  const formatRelativeTime = useFormatRelativeTime();
  const [grantRequests, grantDetails] = useQuery(
    queries.pendingGrantRequests()
  );
  const [pushLiveRequests, pushLiveDetails] = useQuery(
    queries.pendingPushLiveRequests()
  );
  const [currentUser] = useQuery(queries.currentUser());

  const { q, requester } = useCurrentUrl(routes.monitoring.requests);
  const search = q ?? "";
  const setSearch = (value: string) =>
    navigate(null, { q: value }, { replace: true });
  const requesterFilters = parseMultiFilter(requester);
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.monitoring.requests,
    { name: "asc", requester: "asc", createdAt: "desc" }
  );

  const entries: {
    createdAt: number;
    title: string;
    requesterName: string;
    requesterId: string | null;
    requesterUser: ComponentProps<typeof Avatar>["user"] | null;
    item: RequestRow;
  }[] = [];

  for (const request of grantRequests) {
    const connector = request.scopeAsConnector;
    if (!connector) continue;
    const requester = request.requestedByUser
      ? userDisplayName(request.requestedByUser)
      : "Someone";
    const isMine = currentUser?.id === request.requestedByUserId;
    entries.push({
      createdAt: request.createdAt,
      title: connector.name,
      requesterName: requester,
      requesterId: request.requestedByUserId ?? null,
      requesterUser: request.requestedByUser ?? null,
      item: {
        key: `grant:${request.id}`,
        name: (hover) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              minWidth: 0,
            }}
          >
            <RequestIcon requester={request.requestedByUser}>
              <ConnectorLogoContainer
                size={32}
                hover={hover}
                borderRadius={8}
                outline={false}
                hoverBackground={false}
              >
                <ConnectorLogo app={connector} size={16} faded={false} />
              </ConnectorLogoContainer>
            </RequestIcon>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minWidth: 0,
              }}
            >
              <Text
                ellipsis
                variant="text-regular-md"
                color="var(--text-primary)"
              >
                {connector.name}
              </Text>
              {cellText("Connector access", "var(--text-secondary)")}
            </div>
          </div>
        ),
        requester: cellText(requester, "var(--text-primary)"),
        createdAt: requestedWhen(request.createdAt, formatRelativeTime),
        actions: requestActions(
          isMine
            ? [
                {
                  icon: "undo-2",
                  label: "Cancel request",
                  onClick: () =>
                    z.mutate(
                      sharedMutators.cancelGrantRequest({
                        requestId: request.id,
                      })
                    ),
                },
              ]
            : [
                {
                  icon: "check",
                  label: "Approve",
                  onClick: () =>
                    z.mutate(
                      sharedMutators.approveGrantRequest({
                        requestId: request.id,
                      })
                    ),
                },
                {
                  icon: "x",
                  label: "Decline",
                  onClick: () =>
                    setDecliningRequest({
                      id: request.id,
                      connectorName: connector.name,
                    }),
                },
              ]
        ),
        link: makeUrl(routes.modal.connector, {
          id: connector.id,
          tab: "access",
        }),
      },
    });
  }

  for (const request of pushLiveRequests) {
    const branch = request.branch;
    const workflow = branch?.workflow;
    if (!branch || !workflow || request.commitId !== branch.headCommitId)
      continue;
    const requester = request.requestedByUser
      ? userDisplayName(request.requestedByUser)
      : "Someone";
    const isMine = currentUser?.id === request.requestedByUserId;
    if (request.approvedByUserId && !isMine) continue;
    entries.push({
      createdAt: request.createdAt,
      title: workflow.name,
      requesterName: requester,
      requesterId: request.requestedByUserId,
      requesterUser: request.requestedByUser ?? null,
      item: {
        key: `pushlive:${request.id}`,
        name: (hover) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              minWidth: 0,
            }}
          >
            <RequestIcon requester={request.requestedByUser}>
              <WorkflowBadge workflow={workflow} hover={hover} />
            </RequestIcon>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minWidth: 0,
              }}
            >
              <Text
                ellipsis
                variant="text-regular-md"
                color="var(--text-primary)"
              >
                {workflow.name}
              </Text>
              {cellText(
                request.approvedByUser
                  ? `Approved by ${userDisplayName(request.approvedByUser)}, ready to push live`
                  : `Push request from ${branch.name}`,
                "var(--text-secondary)"
              )}
            </div>
          </div>
        ),
        requester: cellText(requester, "var(--text-primary)"),
        createdAt: requestedWhen(request.createdAt, formatRelativeTime),
        actions: requestActions(
          isMine
            ? [
                {
                  icon: "undo-2",
                  label: "Cancel request",
                  onClick: () =>
                    z.mutate(
                      sharedMutators.cancelPushLiveRequest({
                        requestId: request.id,
                      })
                    ),
                },
              ]
            : [
                {
                  icon: "check",
                  label: "Approve",
                  onClick: async () => {
                    const result = await z.mutate(
                      sharedMutators.approvePushLiveRequest({
                        requestId: request.id,
                      })
                    ).server;
                    if (result.type === "error")
                      showToast({
                        icon: "circle-off",
                        message: result.error.message,
                        buttonLabel: "Dismiss",
                      });
                  },
                },
                {
                  icon: "x",
                  label: "Decline",
                  onClick: () =>
                    z.mutate(
                      sharedMutators.declinePushLiveRequest({
                        requestId: request.id,
                      })
                    ),
                },
              ]
        ),
        link: makeUrl(routes.space.workflow, {
          spaceId: workflow.spaceId,
          workflowId: workflow.id,
          branchId: branch.id,
        }),
      },
    });
  }

  const requestersById = new Map<
    string,
    ComponentProps<typeof Avatar>["user"]
  >();
  for (const entry of entries) {
    if (entry.requesterId && entry.requesterUser)
      requestersById.set(entry.requesterId, entry.requesterUser);
  }
  const requesterOptions = [...requestersById.entries()].sort(([, a], [, b]) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const filters: FilterDef[] = [
    {
      kind: "multi",
      key: "requester",
      label: "Requested by",
      icon: <Icon name="circle-user-round" size={16} />,
      options: requesterOptions.map(([id, user]) => ({
        id,
        label: userDisplayName(user),
        menuLeft: <Avatar user={user} size={16} />,
        pillLeft: <Avatar user={user} size={12} />,
      })),
      values: requesterFilters,
      onToggle: (id) =>
        navigate(
          null,
          { requester: toggleMultiFilter(requesterFilters, id) },
          { replace: true }
        ),
    },
  ];

  const needle = search.trim().toLowerCase();
  const items = entries
    .filter((entry) => {
      if (
        needle &&
        !entry.title.toLowerCase().includes(needle) &&
        !entry.requesterName.toLowerCase().includes(needle)
      )
        return false;
      if (
        requesterFilters.length > 0 &&
        (entry.requesterId == null ||
          !requesterFilters.includes(entry.requesterId))
      )
        return false;
      return true;
    })
    .sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      switch (sortColumn) {
        case "name":
          return a.title.localeCompare(b.title) * dir;
        case "requester":
          return a.requesterName.localeCompare(b.requesterName) * dir;
        case "createdAt":
          return (a.createdAt - b.createdAt) * dir;
        default:
          return b.createdAt - a.createdAt;
      }
    })
    .map((entry) => entry.item);

  const loaded =
    grantDetails?.type === "complete" && pushLiveDetails?.type === "complete";

  return (
    <MonitoringLayout
      ready={loaded}
      headerDivider
      headerItem={
        <HeaderSearch>
          <SearchField
            placeholder="Search requests…"
            value={search}
            onChange={setSearch}
          />
        </HeaderSearch>
      }
    >
      <ListTable
        style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
        columns={{ requester: "md", createdAt: "md" }}
        headers={{
          name: "Request",
          requester: "From",
          createdAt: "When",
          actions: "",
        }}
        sortableColumns={sortableColumns}
        dateColumns={["createdAt"]}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        headerActions={<ListFilter filters={filters} />}
        footer={
          hasActiveFilters(filters) ? (
            <ListFilterPills filters={filters} />
          ) : null
        }
        emptyState={
          loaded ? (
            entries.length > 0 ? (
              <EmptyState
                iconName="list-todo"
                title="No matching requests"
                description="Try a different search or filter."
              />
            ) : (
              <EmptyState
                iconName="heart-handshake"
                title="No pending requests"
                description="Requests will appear here for you to review or track."
              />
            )
          ) : undefined
        }
        items={items}
      />
      {decliningRequest && (
        <DeclineGrantRequestModal
          key={decliningRequest.id}
          requestId={decliningRequest.id}
          connectorName={decliningRequest.connectorName}
          onClose={() => setDecliningRequest(null)}
          onDeclined={() => setDecliningRequest(null)}
        />
      )}
    </MonitoringLayout>
  );
}
